import {
  isSourceClassLabel,
  type SourceClassLabel,
} from '../source-class-label';
import {
  GOVERNED_POLICY_ENFORCEMENT_RECEIPT_EVIDENCE_KIND,
  GOVERNED_POLICY_ENFORCEMENT_SOURCE_CLASS,
} from './contracts/neurotruth-policy-projection';

export type AskHelixProofAdmissionInput = Readonly<{
  proofLevel?: string | null;
  source?: string | null;
  sourceClass?: string | null;
  sourceId?: string | null;
  evidenceKind?: string | null;
  proofRefId?: string | null;
  label?: string | null;
}>;

export type AskHelixProofAdmissionResult = Readonly<{
  display: boolean;
  activation: boolean;
}>;

export type AskHelixPublicProofSourceAdmissionAccepted = Readonly<{
  accepted: true;
  sourceClass: string;
  basis: 'raw_source_class' | 'source_class_label';
  sourceClassLabel?: SourceClassLabel;
}>;

export type AskHelixPublicProofSourceAdmissionRejected = Readonly<{
  accepted: false;
  sourceClass: null;
  reasonCode:
    | 'missing_source_class'
    | 'invalid_source_class_label'
    | 'unapproved_source_class_label';
  message: string;
  sourceClassLabel?: SourceClassLabel;
}>;

export type AskHelixPublicProofSourceAdmissionResult =
  | AskHelixPublicProofSourceAdmissionAccepted
  | AskHelixPublicProofSourceAdmissionRejected;

const APPROVED_GOVERNED_SOURCE_CLASSES = Object.freeze(
  new Set([
    'connector-derived',
    'csv-admin-upload',
    'modeled',
    'system-of-record',
    'approved-source',
    'approved_source',
    'public-filing',
    'decision-ledger-verification-plan',
  ]),
);

const ACTIVATION_ELIGIBLE_SOURCE_CLASSES = Object.freeze(
  new Set([
    'csv-admin-upload',
    'modeled',
    'system-of-record',
    'approved-source',
    'approved_source',
    'decision-ledger-verification-plan',
  ]),
);

const FAKE_LINEAGE_TOKENS = Object.freeze(
  new Set([
    'fixture',
    'fixtures',
    'synthetic',
    'fake',
    'demo',
    'mock',
    'test',
    'uat',
    'public',
    'manual-admin',
    'manual_admin',
    'component-local',
    'component_local',
    'local',
    'local-only',
    'local_only',
    'pending',
  ]),
);

const POSTURE_SOURCE_CLASS_TOKENS = Object.freeze(
  new Set([
    'inferred',
    'assumption',
    'assumptions',
    'gap',
    'gaps',
    'heuristic',
    'placeholder',
    'sample',
    'stub',
    'observed',
    'draft',
  ]),
);

const FAKE_LINEAGE_TOKEN_PATTERN = tokenPattern(FAKE_LINEAGE_TOKENS);
const POSTURE_SOURCE_CLASS_TOKEN_PATTERN = tokenPattern(POSTURE_SOURCE_CLASS_TOKENS);

export function admitProofRef(
  input: AskHelixProofAdmissionInput,
): AskHelixProofAdmissionResult {
  const proofLevel = normalizeProofAdmissionToken(input.proofLevel);
  const sourceClass = normalizeProofAdmissionToken(input.sourceClass);
  const sourceId = normalizeProofAdmissionToken(input.sourceId);
  // Labels are display prose; prose never vetoes proof. Only structural lineage
  // fields (source, sourceId, evidenceKind, proofRefId) and the sourceClass are
  // scanned for fake-lineage tokens.
  const hasForbiddenLineageToken =
    [
      input.source,
      input.sourceId,
      input.evidenceKind,
      input.proofRefId,
    ].some(hasForbiddenProofSourceToken) ||
    hasForbiddenProofSourceClassToken(input.sourceClass);
  const hasRequiredSourceLineage = sourceId !== null;
  const sourceIsDisplayApproved =
    sourceClass !== null &&
    (isGovernedPolicyEnforcementReceiptProof(input) ||
      isApprovedGovernedProofSourceClass(sourceClass)) &&
    hasRequiredSourceLineage &&
    !hasForbiddenLineageToken;
  const sourceIsActivationApproved =
    sourceClass !== null &&
    isActivationEligibleProofSourceClass(sourceClass) &&
    hasRequiredSourceLineage &&
    !hasForbiddenLineageToken;

  const verifiedDisplay = proofLevel === 'verified' && sourceIsDisplayApproved;
  const verifiedActivation = proofLevel === 'verified' && sourceIsActivationApproved;
  const connectorDerivedDisplay =
    proofLevel === 'connector-derived' && sourceIsDisplayApproved;

  return {
    display: verifiedDisplay || connectorDerivedDisplay,
    activation: verifiedActivation,
  };
}

export function isGovernedPolicyEnforcementReceiptProof(input: {
  sourceClass?: unknown;
  evidenceKind?: unknown;
}): boolean {
  return (
    normalizeProofAdmissionToken(input.sourceClass) ===
      GOVERNED_POLICY_ENFORCEMENT_SOURCE_CLASS &&
    normalizeProofAdmissionToken(input.evidenceKind) ===
      GOVERNED_POLICY_ENFORCEMENT_RECEIPT_EVIDENCE_KIND
  );
}

export function isApprovedGovernedProofSourceClass(value: unknown): boolean {
  const normalized = normalizeProofAdmissionToken(value);
  if (!normalized) {
    return false;
  }

  if (normalized === GOVERNED_POLICY_ENFORCEMENT_SOURCE_CLASS) {
    return false;
  }

  // Exact approved-class membership is authoritative and is decided before the
  // forbidden-token scan, so an exactly-approved governed class (e.g.
  // 'public-filing') is never vetoed by a substring token match such as the
  // fake-lineage token 'public'.
  if (APPROVED_GOVERNED_SOURCE_CLASSES.has(normalized)) {
    return true;
  }

  // Pattern-derived classes (approved_ prefix, governed shape) remain subject to
  // the forbidden-token scan so posture/fake tokens still veto them.
  if (hasForbiddenProofSourceClassToken(normalized)) {
    return false;
  }

  return (
    isApprovedPrefixSourceClass(normalized) ||
    isGovernedShapeSourceClass(normalized)
  );
}

export function resolveAskHelixPublicProofSourceClass(input: {
  sourceClass?: unknown;
  sourceClassLabel?: unknown;
}): AskHelixPublicProofSourceAdmissionResult {
  const rawSourceClass = readProofAdmissionText(input.sourceClass);
  if (rawSourceClass) {
    return {
      accepted: true,
      sourceClass: rawSourceClass,
      basis: 'raw_source_class',
      ...(isSourceClassLabel(input.sourceClassLabel)
        ? { sourceClassLabel: input.sourceClassLabel }
        : {}),
    };
  }

  const rawSourceClassLabel = readProofAdmissionText(input.sourceClassLabel);
  if (!rawSourceClassLabel) {
    return {
      accepted: false,
      sourceClass: null,
      reasonCode: 'missing_source_class',
      message:
        'Proof sourceClass was redacted and no closed sourceClassLabel was supplied.',
    };
  }

  if (!isSourceClassLabel(rawSourceClassLabel)) {
    return {
      accepted: false,
      sourceClass: null,
      reasonCode: 'invalid_source_class_label',
      message:
        'Proof sourceClass was redacted and sourceClassLabel was not in the closed public enum.',
    };
  }

  if (!isApprovedGovernedProofSourceClass(rawSourceClassLabel)) {
    return {
      accepted: false,
      sourceClass: null,
      reasonCode: 'unapproved_source_class_label',
      sourceClassLabel: rawSourceClassLabel,
      message:
        'Proof sourceClass was redacted and sourceClassLabel is not display-approved.',
    };
  }

  return {
    accepted: true,
    sourceClass: rawSourceClassLabel,
    sourceClassLabel: rawSourceClassLabel,
    basis: 'source_class_label',
  };
}

function isActivationEligibleProofSourceClass(value: unknown): boolean {
  const normalized = normalizeProofAdmissionToken(value);
  if (!normalized) {
    return false;
  }

  // Exact activation-eligible membership is decided before the forbidden-token
  // scan, mirroring the display path.
  if (ACTIVATION_ELIGIBLE_SOURCE_CLASSES.has(normalized)) {
    return true;
  }

  if (normalized === GOVERNED_POLICY_ENFORCEMENT_SOURCE_CLASS) {
    return false;
  }

  if (hasForbiddenProofSourceClassToken(normalized)) {
    return false;
  }

  return (
    isApprovedPrefixSourceClass(normalized) ||
    isGovernedShapeSourceClass(normalized)
  );
}

export function hasForbiddenProofSourceToken(value: unknown): boolean {
  return hasForbiddenToken(value, FAKE_LINEAGE_TOKEN_PATTERN);
}

function hasForbiddenProofSourceClassToken(value: unknown): boolean {
  return (
    hasForbiddenToken(value, FAKE_LINEAGE_TOKEN_PATTERN) ||
    hasForbiddenToken(value, POSTURE_SOURCE_CLASS_TOKEN_PATTERN)
  );
}

function hasForbiddenToken(value: unknown, pattern: RegExp): boolean {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : null;
  if (!normalized) {
    return false;
  }

  return pattern.test(normalized);
}

function tokenPattern(tokens: ReadonlySet<string>): RegExp {
  // The 'public' fake-lineage token is carved out for '-filing'. Exact
  // sourceClass membership for 'public-filing' is now decided above this scan,
  // but the carve-out is still required for the structural-field scan so a
  // structural field legitimately referencing a public filing (e.g. a sourceId
  // like 'finance-pnl/public-filing/sha256:...') is not vetoed by bare 'public'.
  const alternatives = Array.from(tokens).map((token) =>
    token === 'public' ? `${escapeRegExp(token)}(?!-filing)` : escapeRegExp(token),
  );

  return new RegExp(
    `(?:^|[^a-z0-9])(?:${alternatives.join('|')})(?:$|[^a-z0-9])`,
    'i',
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isApprovedPrefixSourceClass(value: string): boolean {
  return value.startsWith('approved_');
}

function isGovernedShapeSourceClass(value: string): boolean {
  return (
    value === 'governed' ||
    value.startsWith('governed_') ||
    value.startsWith('governed-') ||
    value.includes('governed_') ||
    value.includes('governed-')
  );
}

function normalizeProofAdmissionToken(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim().toLowerCase()
    : null;
}

function readProofAdmissionText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}
